import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { PACKAGES } from "@/config/brand";
import { useTracking } from "@/hooks/useTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-2xl border border-white/15 bg-white/[0.06] px-5 py-4 text-sm text-white placeholder:text-white/40 outline-none transition-colors duration-300 focus:border-sdh-sky";

const EnquiryForm = ({ defaultPackage = "complete-package", dark = false }) => {
  const { getTrackingParams, withTracking } = useTracking();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    suburb: "",
    package: defaultPackage,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, utm: getTrackingParams() });
      navigate(withTracking("/thank-you"));
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="enquiry-form"
      className="rounded-3xl border border-white/12 bg-white/[0.05] p-7 backdrop-blur-sm md:p-9"
    >
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-sdh-sky">
        Get your free quote
      </p>
      <h3 className="mt-2 font-display text-3xl uppercase tracking-wide text-white md:text-4xl">
        Enquire below
      </h3>
      <div className="mt-7 grid gap-4 sm:grid-cols-2">
        <input
          required
          data-testid="enquiry-name-input"
          value={form.full_name}
          onChange={set("full_name")}
          placeholder="Full name"
          className={inputCls}
        />
        <input
          required
          data-testid="enquiry-phone-input"
          value={form.phone}
          onChange={set("phone")}
          placeholder="Phone number"
          type="tel"
          className={inputCls}
        />
        <input
          required
          data-testid="enquiry-email-input"
          value={form.email}
          onChange={set("email")}
          placeholder="Email address"
          type="email"
          className={inputCls}
        />
        <input
          required
          data-testid="enquiry-suburb-input"
          value={form.suburb}
          onChange={set("suburb")}
          placeholder="Your suburb (Sydney)"
          className={inputCls}
        />
        <select
          data-testid="enquiry-package-select"
          value={form.package}
          onChange={set("package")}
          className={`${inputCls} sm:col-span-2 appearance-none [&>option]:bg-[#071735]`}
        >
          {Object.values(PACKAGES).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.price} + GST
            </option>
          ))}
        </select>
        <textarea
          data-testid="enquiry-message-input"
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us about your bathroom (optional)"
          rows={4}
          className={`${inputCls} sm:col-span-2 resize-none`}
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-submit-button"
        className="group mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-sdh-sky px-6 py-4 font-display text-lg uppercase tracking-wider text-[#0B1D39] transition-colors duration-300 hover:bg-white disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Get My Free Quote"}
        <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <p className="mt-4 text-xs leading-relaxed text-white/40">
        No obligation, no pressure. Your details are only used to respond to your enquiry.
      </p>
    </form>
  );
};

export default EnquiryForm;
